import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Download, 
  Loader2,
  Eye,
  Share2,
  FileCheck,
  CheckCircle2,
  AlertCircle,
  Zap,
  X
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { ExtendedLead } from './types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContratosService } from '@/services/contratosService';

const STATUS_CONTRATO_OPTIONS = [
  { value: 'contrato_gerado', label: 'Não assinado' },
  { value: 'assinado', label: 'Assinado' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'finalizado', label: 'Finalizado' },
] as const;

interface Contrato {
  id: string;
  lead_id: string;
  proposta_id: string;
  contrato_pdf_url: string | null;
  status: string;
  created_at: string;
  updated_at?: string;
}

interface Proposta {
  id: string;
  lead_id: string;
  proposta_pdf_url: string | null;
  proposta_aceita: boolean;
  data_proposta_aceita: string | null;
  created_at: string;
  updated_at: string;
  potencia_total_paineis?: number;
  total_paineis?: number;
  potencia_total_inversores?: number;
  kit1_insumos?: any;
  kit2_insumos?: any;
  kit3_insumos?: any;
  kit4_insumos?: any;
}

interface DadosSistema {
  modeloModulo: string;
  potenciaModulo: number;
  numeroModulos: number;
  potenciaTotalSistema: number;
  numeroInversores: number;
  modeloInversor: string;
  potenciaInversor: number;
  potenciaTotalInversores: number;
}

interface ContratosProps {
  leadData: ExtendedLead;
}

export default function Contratos({ leadData }: ContratosProps) {
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [loading, setLoading] = useState(true);
  const [marcandoProposta, setMarcandoProposta] = useState<string | null>(null);
  const [propostaParaMarcar, setPropostaParaMarcar] = useState<Proposta | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [propostaParaDesmarcar, setPropostaParaDesmarcar] = useState<Proposta | null>(null);
  const [showUndoConfirmDialog, setShowUndoConfirmDialog] = useState(false);
  const [atualizandoStatusId, setAtualizandoStatusId] = useState<string | null>(null);
  const [dadosSistema, setDadosSistema] = useState<DadosSistema | null>(null);
  const [loadingSistema, setLoadingSistema] = useState(true);
  const { toast } = useToast();
  const { hasRole } = useAuthorization();
  const isParceiro = hasRole('parceiro');
  const podeAtualizarStatusContrato = hasRole('admin') || hasRole('financeiro');

  const fetchContratos = async () => {
    if (!leadData?.id) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('contratos_sun7')
        .select('id, lead_id, proposta_id, contrato_pdf_url, status, created_at')
        .eq('lead_id', leadData.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filtrar apenas contratos que possuem contrato_pdf_url
      const contratosComPdf = (data || []).filter(c => c.contrato_pdf_url !== null);
      setContratos(contratosComPdf);
    } catch (error: unknown) {
      const err = error as { message?: string; details?: string; code?: string };
      console.error('Erro ao buscar contratos:', err?.message ?? err, err?.details ?? '');
      toast({
        title: 'Erro',
        description: err?.message ?? 'Não foi possível carregar os contratos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPropostas = async () => {
    try {
      const { data, error } = await supabase
        .from('propostas_clientes')
        .select('id, lead_id, proposta_pdf_url, proposta_aceita, data_proposta_aceita, created_at, updated_at, potencia_total_paineis, total_paineis, potencia_total_inversores, kit1_insumos, kit2_insumos, kit3_insumos, kit4_insumos')
        .eq('lead_id', leadData.id)
        .not('proposta_pdf_url', 'is', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPropostas(data || []);
      
      // Buscar dados do sistema da proposta aceita
      const propostaAceita = (data || []).find(p => p.proposta_aceita);
      if (propostaAceita) {
        extrairDadosSistema(propostaAceita);
      } else {
        setDadosSistema(null);
        setLoadingSistema(false);
      }
    } catch (error) {
      console.error('Erro ao buscar propostas:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as propostas',
        variant: 'destructive',
      });
    }
  };

  const extrairDadosSistema = (proposta: Proposta) => {
    try {
      setLoadingSistema(true);
      
      let modeloModulo = 'Não informado';
      let potenciaModulo = 0;
      const numeroModulos = proposta.total_paineis || 0;
      const potenciaTotalSistema = proposta.potencia_total_paineis || 0;
      let numeroInversores = 0;
      let modeloInversor = 'Não informado';
      let potenciaInversor = 0;
      const potenciaTotalInversores = proposta.potencia_total_inversores || 0;

      // Processar insumos dos kits
      const kits = [
        proposta.kit1_insumos,
        proposta.kit2_insumos,
        proposta.kit3_insumos,
        proposta.kit4_insumos
      ].filter(kit => kit != null);

      let moduloEncontrado = false;
      let inversorEncontrado = false;

      kits.forEach((kit: any) => {
        if (!kit.Insumos || !Array.isArray(kit.Insumos)) return;

        kit.Insumos.forEach((insumo: any) => {
          const tipo = insumo.Tipo || insumo.tipo || '';
          const tipoLower = tipo.toLowerCase();

          // Buscar módulo
          if (!moduloEncontrado && (tipoLower.includes('painel') || tipoLower.includes('módulo') || tipoLower.includes('modulo'))) {
            modeloModulo = insumo.Descricao || insumo.descricao || insumo.Nome || insumo.nome || 'Não informado';
            potenciaModulo = parseFloat((insumo.Potencia || insumo.potencia || '0').toString()) || 0;
            // Converter de W para kW se necessário
            if (potenciaModulo > 1000) {
              potenciaModulo = potenciaModulo / 1000;
            }
            moduloEncontrado = true;
          }

          // Buscar inversor
          if ((tipoLower.includes('inversor') || tipoLower.includes('microinversor'))) {
            if (!inversorEncontrado) {
              modeloInversor = insumo.Descricao || insumo.descricao || insumo.Nome || insumo.nome || 'Não informado';
              potenciaInversor = parseFloat((insumo.Potencia || insumo.potencia || '0').toString()) || 0;
              // Converter de W para kW se necessário
              if (potenciaInversor > 1000) {
                potenciaInversor = potenciaInversor / 1000;
              }
              inversorEncontrado = true;
            }
            // Contar número de inversores
            const quantidade = parseInt((insumo.Quantidade || insumo.quantidade || '0').toString()) || 0;
            numeroInversores += quantidade;
          }
        });
      });

      // Se não encontrou número de inversores nos insumos, tentar calcular pela potência
      if (numeroInversores === 0 && potenciaInversor > 0 && potenciaTotalInversores > 0) {
        numeroInversores = Math.round(potenciaTotalInversores / potenciaInversor);
      }

      setDadosSistema({
        modeloModulo,
        potenciaModulo,
        numeroModulos,
        potenciaTotalSistema,
        numeroInversores,
        modeloInversor,
        potenciaInversor,
        potenciaTotalInversores
      });
    } catch (error) {
      console.error('Erro ao extrair dados do sistema:', error);
      setDadosSistema(null);
    } finally {
      setLoadingSistema(false);
    }
  };

  useEffect(() => {
    if (leadData.id) {
      fetchContratos();
      fetchPropostas();
    }
  }, [leadData.id]);

  const handleMarcarPropostaAceita = async () => {
    if (!propostaParaMarcar) return;

    try {
      setMarcandoProposta(propostaParaMarcar.id);
      
      // Se já existe uma proposta aceita, desmarcar ela primeiro
      const propostaAceitaAtual = propostas.find(p => p.proposta_aceita && p.id !== propostaParaMarcar.id);
      if (propostaAceitaAtual) {
        const { error: errorUnmark } = await supabase
          .from('propostas_clientes')
          .update({ proposta_aceita: false, data_proposta_aceita: null })
          .eq('id', propostaAceitaAtual.id);

        if (errorUnmark) throw errorUnmark;
      }

      // Marcar a nova proposta como aceita
      const { error } = await supabase
        .from('propostas_clientes')
        .update({ 
          proposta_aceita: true, 
          data_proposta_aceita: new Date().toISOString() 
        })
        .eq('id', propostaParaMarcar.id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: 'Proposta marcada como aceita com sucesso',
      });

      setShowConfirmDialog(false);
      setPropostaParaMarcar(null);
      await fetchPropostas();
      // Os dados do sistema serão atualizados automaticamente pelo fetchPropostas
    } catch (error) {
      console.error('Erro ao marcar proposta como aceita:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível marcar a proposta como aceita',
        variant: 'destructive',
      });
    } finally {
      setMarcandoProposta(null);
    }
  };

  const handleDesmarcarPropostaAceita = async () => {
    if (!propostaParaDesmarcar) return;

    try {
      setMarcandoProposta(propostaParaDesmarcar.id);
      
      const { error } = await supabase
        .from('propostas_clientes')
        .update({ 
          proposta_aceita: false, 
          data_proposta_aceita: null 
        })
        .eq('id', propostaParaDesmarcar.id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: 'Aceite da proposta desfeito com sucesso',
      });

      setShowUndoConfirmDialog(false);
      setPropostaParaDesmarcar(null);
      await fetchPropostas();
    } catch (error) {
      console.error('Erro ao desfazer aceite da proposta:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível desfazer o aceite da proposta',
        variant: 'destructive',
      });
    } finally {
      setMarcandoProposta(null);
    }
  };

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  const handleShare = (url: string) => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(url)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleAtualizarStatusContrato = async (contratoId: string, status: string) => {
    setAtualizandoStatusId(contratoId);
    try {
      await ContratosService.atualizarStatusContrato(contratoId, status);
      setContratos((prev) =>
        prev.map((c) => (c.id === contratoId ? { ...c, status } : c))
      );
      toast({
        title: 'Status atualizado',
        description: status === 'assinado' ? 'Contrato marcado como assinado.' : 'Status do contrato atualizado.',
      });
    } catch (err) {
      toast({
        title: 'Erro',
        description: err instanceof Error ? err.message : 'Não foi possível atualizar o status',
        variant: 'destructive',
      });
    } finally {
      setAtualizandoStatusId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileName = (url: string) => {
    return url.split('/').pop() || 'contrato.pdf';
  };

  const getStatusLabel = (status: string) => {
    const fromOptions = STATUS_CONTRATO_OPTIONS.find((o) => o.value === status);
    if (fromOptions) return fromOptions.label;
    const statusMap: Record<string, string> = {
      'rascunho': 'Rascunho',
      'proposta_aceita': 'Proposta Aceita',
      'contrato_gerado': 'Não assinado',
      'assinado': 'Assinado',
      'em_andamento': 'Em andamento',
      'finalizado': 'Finalizado',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      'rascunho': 'text-gray-500',
      'proposta_aceita': 'text-blue-500',
      'contrato_gerado': 'text-orange-500',
      'assinado': 'text-green-500',
      'em_andamento': 'text-blue-600',
      'finalizado': 'text-emerald-700',
    };
    return colorMap[status] || 'text-gray-500';
  };

  const statusSelectValue = (status: string) => {
    if (STATUS_CONTRATO_OPTIONS.some((o) => o.value === status)) return status;
    return 'contrato_gerado';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const propostasAceitas = propostas.filter(p => p.proposta_aceita);
  const propostasNaoAceitas = propostas.filter(p => !p.proposta_aceita);
  const temMultiplasPropostas = propostas.length > 1;

  const formatNumber = (value: number | null | undefined, unit: string = '', decimals: number = 2) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value) + (unit ? ` ${unit}` : '');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Conteúdo Principal - Ocupa 7 colunas (aprox. 60%) */}
      <div className="lg:col-span-7 space-y-6 min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Contratos do Cliente</h3>
          <p className="text-sm text-muted-foreground">
            {temMultiplasPropostas 
              ? `${propostas.length} proposta(s) disponível(is) - ${propostasAceitas.length} aceita(s)`
              : 'Contratos gerados na página Propostas'
            }
          </p>
        </div>
      </div>

      {/* Seção de Propostas - Mostrar quando há propostas */}
      {propostas.length > 0 && (
        <div className="space-y-4">
          <div>
            <h4 className="text-md font-semibold mb-3">Propostas do Cliente</h4>
            <p className="text-sm text-muted-foreground mb-4">
              {temMultiplasPropostas 
                ? 'Selecione qual proposta foi aceita pelo cliente. Esta será a base para gerar o contrato.'
                : 'Esta proposta será usada como base para gerar o contrato.'
              }
            </p>
          </div>

          {/* Proposta Aceita */}
          {propostasAceitas.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-sm font-medium text-green-700 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Proposta Aceita (Base para Contrato)
              </h5>
              <div className="flex flex-wrap gap-4">
                {propostasAceitas.map((proposta) => (
                  <Card key={proposta.id} className="border-green-500 border-2 bg-green-50/50 w-full max-w-[280px]">
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-3">
                        {/* Cabeçalho do Card: Ícone + Nome + Badge */}
                        <div className="flex flex-col items-center text-center gap-2">
                          <div className="flex-shrink-0">
                            <FileText className="h-12 w-12 text-green-600" />
                          </div>
                          <div className="w-full min-w-0">
                            <h6 className="text-sm font-bold text-gray-900 break-words line-clamp-2 mb-1" title={proposta.proposta_pdf_url ? getFileName(proposta.proposta_pdf_url) : 'Proposta'}>
                              {proposta.proposta_pdf_url ? getFileName(proposta.proposta_pdf_url) : 'Proposta'}
                            </h6>
                            <Badge className="bg-green-600 text-white">Aceita</Badge>
                          </div>
                        </div>

                        {/* Datas e Informações */}
                        <div className="flex flex-col gap-1.5 text-[11px] text-muted-foreground border-t border-green-100 pt-3">
                          <p className="flex justify-between items-center">
                            <span className="font-semibold text-gray-600">Gerada em:</span>
                            <span>{formatDate(proposta.created_at)}</span>
                          </p>
                          {proposta.data_proposta_aceita && (
                            <p className="flex justify-between items-center text-green-700 font-medium bg-green-100/50 px-2 py-0.5 rounded">
                              <span className="font-semibold">Aceita em:</span>
                              <span>{formatDate(proposta.data_proposta_aceita)}</span>
                            </p>
                          )}
                        </div>
                        
                        {/* Botões de Ação na Base */}
                        <div className="flex items-center gap-2 pt-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => proposta.proposta_pdf_url && handleDownload(proposta.proposta_pdf_url)}
                            disabled={!proposta.proposta_pdf_url || isParceiro}
                            className="flex-1 h-9 text-xs border-green-200 hover:bg-green-100 hover:text-green-800"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Ver
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => proposta.proposta_pdf_url && handleDownload(proposta.proposta_pdf_url)}
                            disabled={!proposta.proposta_pdf_url || isParceiro}
                            className="h-9 w-9 border-green-200 hover:bg-green-100 hover:text-green-800"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {!isParceiro && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setPropostaParaDesmarcar(proposta);
                                setShowUndoConfirmDialog(true);
                              }}
                              className="h-9 w-9 p-0 border-red-200 hover:bg-red-100 hover:text-red-800 text-red-600"
                              disabled={marcandoProposta === proposta.id}
                              title="Desfazer aceite"
                            >
                              {marcandoProposta === proposta.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <X className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Propostas Não Aceitas */}
          {propostasNaoAceitas.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {temMultiplasPropostas ? 'Outras Propostas' : 'Proposta'}
              </h5>
              <div className="flex flex-wrap gap-4">
                {propostasNaoAceitas.map((proposta) => (
                  <Card key={proposta.id} className="hover:shadow-md transition-shadow w-full max-w-[280px]">
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-3">
                        {/* Cabeçalho do Card: Ícone + Nome */}
                        <div className="flex flex-col items-center text-center gap-2">
                          <div className="flex-shrink-0">
                            <FileText className="h-12 w-12 text-red-500" />
                          </div>
                          <div className="w-full min-w-0">
                            <h6 className="text-sm font-bold text-gray-900 break-words line-clamp-2 mb-1" title={proposta.proposta_pdf_url ? getFileName(proposta.proposta_pdf_url) : 'Proposta'}>
                              {proposta.proposta_pdf_url ? getFileName(proposta.proposta_pdf_url) : 'Proposta'}
                            </h6>
                            <Badge variant="outline" className="text-[10px] font-normal">Proposta Comercial</Badge>
                          </div>
                        </div>

                        {/* Informações */}
                        <div className="flex flex-col gap-1.5 text-[11px] text-muted-foreground border-t pt-3">
                          <p className="flex justify-between items-center">
                            <span className="font-semibold text-gray-600">Gerada em:</span>
                            <span>{formatDate(proposta.created_at)}</span>
                          </p>
                        </div>

                        {/* Botões de Ação na Base */}
                        <div className="flex items-center gap-2 pt-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => proposta.proposta_pdf_url && handleDownload(proposta.proposta_pdf_url)}
                            className="flex-1 h-9 text-xs"
                            disabled={!proposta.proposta_pdf_url || isParceiro}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Ver
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => proposta.proposta_pdf_url && handleDownload(proposta.proposta_pdf_url)}
                            className="h-9 w-9 p-0"
                            disabled={!proposta.proposta_pdf_url || isParceiro}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {!isParceiro && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => {
                                setPropostaParaMarcar(proposta);
                                setShowConfirmDialog(true);
                              }}
                              className="h-9 w-9 p-0 bg-blue-600 hover:bg-blue-700 text-white"
                              disabled={marcandoProposta === proposta.id}
                            >
                              {marcandoProposta === proposta.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Mensagem quando não há proposta aceita e há múltiplas propostas */}
          {propostasAceitas.length === 0 && temMultiplasPropostas && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-yellow-700 font-medium">
                Nenhuma proposta foi marcada como aceita. Selecione uma proposta acima para indicar qual foi aceita pelo cliente.
              </p>
            </div>
          )}

          {/* Quando há apenas uma proposta, marcar automaticamente como aceita se ainda não estiver marcada */}
          {propostas.length === 1 && !propostas[0].proposta_aceita && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-blue-700 font-medium mb-1">
                  Esta proposta ainda não foi marcada como aceita.
                </p>
                {!isParceiro && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPropostaParaMarcar(propostas[0]);
                      setShowConfirmDialog(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600"
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Marcar como Aceita
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Divisor */}
      {propostas.length > 0 && contratos.length > 0 && (
        <div className="border-t pt-6">
          <h4 className="text-md font-semibold mb-3">Contratos Gerados</h4>
        </div>
      )}

      {/* Lista de contratos */}
      {contratos.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileCheck className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum contrato encontrado</h3>
            <p className="text-muted-foreground text-center max-w-md mb-4">
              Este cliente ainda não possui contratos gerados. Acesse a página de Propostas para criar um contrato para este cliente.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-wrap gap-4">
          {contratos.map((contrato) => (
            <Card key={contrato.id} className="hover:shadow-md transition-shadow w-full max-w-[280px]">
              <CardContent className="p-4">
                <div className="flex flex-col gap-3">
                  {/* Cabeçalho do Card: Ícone + Nome */}
                  <div className="flex flex-col items-center text-center gap-2">
                    <div className="flex-shrink-0">
                      <FileCheck className="h-12 w-12 text-green-500" />
                    </div>
                    <div className="w-full min-w-0">
                      <h6 className="text-sm font-bold text-gray-900 break-words line-clamp-2 mb-1" title={contrato.contrato_pdf_url ? getFileName(contrato.contrato_pdf_url) : 'Contrato'}>
                        {contrato.contrato_pdf_url ? getFileName(contrato.contrato_pdf_url) : 'Contrato'}
                      </h6>
                      <Badge variant="secondary" className="text-[10px] font-normal">Contrato Solar</Badge>
                    </div>
                  </div>

                  {/* Status e Informações */}
                  <div className="flex flex-col gap-1.5 text-[11px] text-muted-foreground border-t pt-3">
                    <p className="flex justify-between items-center">
                      <span className="font-semibold text-gray-600">Gerado em:</span>
                      <span>{formatDate(contrato.created_at)}</span>
                    </p>
                    {podeAtualizarStatusContrato && !isParceiro ? (
                      <div className="flex flex-col gap-1 pt-1">
                        <Label className="text-[11px] font-semibold text-gray-600">Contrato assinado?</Label>
                        <Select
                          value={statusSelectValue(contrato.status)}
                          onValueChange={(v) => handleAtualizarStatusContrato(contrato.id, v)}
                          disabled={atualizandoStatusId === contrato.id}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_CONTRATO_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <p className="flex justify-between items-center">
                        <span className="font-semibold text-gray-600">Status:</span>
                        <span className={`font-bold ${getStatusColor(contrato.status)}`}>
                          {getStatusLabel(contrato.status)}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Botões de Ação na Base */}
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => contrato.contrato_pdf_url && handleDownload(contrato.contrato_pdf_url)}
                      className="flex-1 h-9 text-xs"
                      disabled={!contrato.contrato_pdf_url}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Ver
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => contrato.contrato_pdf_url && handleDownload(contrato.contrato_pdf_url)}
                      className="h-9 w-9 p-0"
                      disabled={!contrato.contrato_pdf_url}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => contrato.contrato_pdf_url && handleShare(contrato.contrato_pdf_url)}
                      className="h-9 w-9 p-0"
                      disabled={!contrato.contrato_pdf_url}
                    >
                      <Share2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de Confirmação */}
      </div>

      {/* Resumo do Sistema - Lado Direito */}
      <div className="lg:col-span-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Resumo do Sistema
            </CardTitle>
            <CardDescription>
              Dados principais do sistema fotovoltaico
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingSistema ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : dadosSistema ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-muted-foreground">Modelo do Módulo</span>
                    <span className="text-sm font-semibold text-right max-w-[60%] break-words">
                      {dadosSistema.modeloModulo}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-muted-foreground">Potência do Módulo</span>
                    <span className="text-sm font-semibold">
                      {formatNumber(dadosSistema.potenciaModulo, 'kW', dadosSistema.potenciaModulo >= 1 ? 2 : 3)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-muted-foreground">Número de Módulos</span>
                    <span className="text-sm font-semibold">
                      {dadosSistema.numeroModulos || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-muted-foreground">Potência Total (kWp)</span>
                    <span className="text-sm font-semibold text-primary">
                      {formatNumber(dadosSistema.potenciaTotalSistema, 'kWp')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-muted-foreground">Número de Inversores</span>
                    <span className="text-sm font-semibold">
                      {dadosSistema.numeroInversores || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-muted-foreground">Modelo do Inversor</span>
                    <span className="text-sm font-semibold text-right max-w-[60%] break-words">
                      {dadosSistema.modeloInversor}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium text-muted-foreground">Potência do Inversor</span>
                    <span className="text-sm font-semibold">
                      {formatNumber(dadosSistema.potenciaInversor, 'kW', dadosSistema.potenciaInversor >= 1 ? 2 : 3)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-muted-foreground">Potência Total (kW)</span>
                    <span className="text-sm font-semibold text-primary">
                      {formatNumber(dadosSistema.potenciaTotalInversores, 'kW')}
                    </span>
                  </div>
                </div>
                <div className="pt-2 text-xs text-muted-foreground italic">
                  * Dados extraídos da proposta aceita
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Zap className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  Dados do sistema não disponíveis
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Aceite uma proposta para visualizar o resumo
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Confirmação */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar Proposta como Aceita</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja marcar esta proposta como aceita pelo cliente?
              {propostasAceitas.length > 0 && (
                <span className="block mt-2 text-yellow-600 font-medium">
                  A proposta atualmente aceita será desmarcada.
                </span>
              )}
              Esta proposta será usada como base para gerar o contrato.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setPropostaParaMarcar(null);
              setShowConfirmDialog(false);
            }}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMarcarPropostaAceita}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={marcandoProposta !== null}
            >
              {marcandoProposta ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Marcando...
                </>
              ) : (
                'Confirmar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Dialog de Confirmação para Desfazer Aceite */}
      <AlertDialog open={showUndoConfirmDialog} onOpenChange={setShowUndoConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desfazer Aceite da Proposta</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja desfazer o aceite desta proposta?
              Isso removerá a proposta como base para os contratos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setPropostaParaDesmarcar(null);
              setShowUndoConfirmDialog(false);
            }}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDesmarcarPropostaAceita}
              className="bg-red-600 hover:bg-red-700"
              disabled={marcandoProposta !== null}
            >
              {marcandoProposta ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Desfazendo...
                </>
              ) : (
                'Desfazer Aceite'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}